import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { loadONNXModel, runInference } from '../utils/onnx';
import { preprocessImageLetterbox } from '../utils/preprocessing';
import ImageViewer from '../components/ImageViewer';
import DetectionList from '../components/DetectionList';
import './TestModel.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

function TestModel() {
  const { modelId: routeModelId } = useParams();
  
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(routeModelId || '');
  const [selectedModel, setSelectedModel] = useState(null);
  const [session, setSession] = useState(null);
  
  const [imageFile, setImageFile] = useState(null);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [detections, setDetections] = useState([]);
  const [selectedDetectionIndex, setSelectedDetectionIndex] = useState(null);
  const [preprocessMetadata, setPreprocessMetadata] = useState(null);
  
  const [confThreshold, setConfThreshold] = useState(0.45);
  const [nmsThreshold, setNmsThreshold] = useState(0.45);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingModel, setLoadingModel] = useState(false);
  const [status, setStatus] = useState('');
  
  const fileInputRef = useRef(null);
  
  // Load models list
  useEffect(() => {
    fetchModels();
  }, []);
  
  // Load model when selected
  useEffect(() => {
    if (selectedModelId) {
      loadModel(selectedModelId);
    }
  }, [selectedModelId]);
  
  async function fetchModels() {
    try {
      const res = await fetch(`${API_URL}/api/models`);
      const data = await res.json();
      setModels(data.models || []);
      
      if (routeModelId && data.models.find(m => m.id === routeModelId)) {
        setSelectedModelId(routeModelId);
      } else if (data.models.length > 0 && !selectedModelId) {
        setSelectedModelId(data.models[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
      setStatus('❌ Failed to load models');
    }
  }
  
  async function loadModel(modelId) {
    setLoadingModel(true);
    setStatus('🔄 Loading model...');
    
    try {
      // Get model info
      const modelRes = await fetch(`${API_URL}/api/models/${modelId}`);
      const modelData = await modelRes.json();
      setSelectedModel(modelData.model);
      
      // Download ONNX model
      const onnxUrl = `${API_URL}/api/models/${modelId}/download/onnx`;
      const onnxSession = await loadONNXModel(onnxUrl);
      setSession(onnxSession);
      
      setStatus(`✅ Model loaded: ${modelData.model.name} v${modelData.model.version}`);
    } catch (error) {
      console.error('Failed to load model:', error);
      setStatus('❌ Failed to load model');
    } finally {
      setLoadingModel(false);
    }
  }
  
  function handleImageSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImageFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageDataUrl(e.target.result);
    };
    reader.readAsDataURL(file);
    
    setDetections([]);
    setSelectedDetectionIndex(null);
  }
  
  async function handleDetect() {
    if (!imageFile || !session || !selectedModel) {
      setStatus('❌ Please select image and model');
      return;
    }
    
    setIsProcessing(true);
    setStatus('🔄 Processing image...');
    setDetections([]);
    
    try {
      // 1. Preprocess image
      setStatus('🖼️ Preprocessing image...');
      const { tensor, metadata } = await preprocessImageLetterbox(
        imageDataUrl,
        selectedModel.input_size || 1280
      );
      
      setPreprocessMetadata(metadata); // Save metadata for coordinate conversion
      
      // 2. Run inference
      setStatus('🤖 Running inference...');
      const output = await runInference(session, tensor);
      
      // 3. Postprocess
      setStatus('🔍 Processing detections...');
      const processedDetections = processYOLOOutput(
        output,
        metadata,
        confThreshold,
        nmsThreshold
      );
      
      setDetections(processedDetections);
      setStatus(`✅ Found ${processedDetections.length} detections`);
      
      // 4. Save to history (optional, автоматически не сохраняем)
      
    } catch (error) {
      console.error('Detection failed:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }
  
  async function handleSaveToHistory() {
    if (!selectedModelId || !detections.length) return;
    
    setStatus('💾 Saving to history...');
    
    try {
      // First, upload image
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('client', 'default');
      
      const uploadRes = await fetch(`${API_URL}/api/uploads/image`, {
        method: 'POST',
        body: formData,
      });
      const uploadData = await uploadRes.json();
      
      // Then, save detection
      const detectionRes = await fetch(`${API_URL}/api/detections/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model_id: selectedModelId,
          image_url: uploadData.image.path,
          detections: detections,
          confidence_threshold: confThreshold,
          client: 'default',
        }),
      });
      
      if (detectionRes.ok) {
        setStatus('✅ Saved to history');
      } else {
        setStatus('❌ Failed to save');
      }
    } catch (error) {
      console.error('Failed to save:', error);
      setStatus('❌ Failed to save');
    }
  }
  
  return (
    <div className="test-model-page">
      <h1>🔬 Test Model</h1>
      
      <div className="controls-section">
        <div className="control-group">
          <label>Model:</label>
          <select
            value={selectedModelId}
            onChange={(e) => setSelectedModelId(e.target.value)}
            disabled={loadingModel}
          >
            <option value="">Select model...</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name} v{model.version} ({model.client})
              </option>
            ))}
          </select>
        </div>
        
        <div className="control-group">
          <label>Confidence Threshold:</label>
          <input
            type="range"
            min="0.1"
            max="0.9"
            step="0.05"
            value={confThreshold}
            onChange={(e) => setConfThreshold(parseFloat(e.target.value))}
          />
          <span>{(confThreshold * 100).toFixed(0)}%</span>
        </div>
        
        <div className="control-group">
          <label>NMS Threshold:</label>
          <input
            type="range"
            min="0.1"
            max="0.9"
            step="0.05"
            value={nmsThreshold}
            onChange={(e) => setNmsThreshold(parseFloat(e.target.value))}
          />
          <span>{(nmsThreshold * 100).toFixed(0)}%</span>
        </div>
      </div>
      
      <div className="action-buttons">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing}
        >
          📁 Select Image
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />
        
        <button
          onClick={handleDetect}
          disabled={!imageFile || !session || isProcessing}
          className="primary"
        >
          {isProcessing ? '⏳ Processing...' : '🚀 Detect'}
        </button>
        
        {detections.length > 0 && (
          <button onClick={handleSaveToHistory}>
            💾 Save to History
          </button>
        )}
      </div>
      
      {status && (
        <div className={`status ${status.startsWith('❌') ? 'error' : ''}`}>
          {status}
        </div>
      )}
      
      <div className="results-section">
        <div className="image-section">
          <h2>Image</h2>
          {imageDataUrl ? (
            <ImageViewer
              imageUrl={imageDataUrl}
              detections={detections}
              selectedIndex={selectedDetectionIndex}
              onDetectionClick={(index) => setSelectedDetectionIndex(index)}
              metadata={preprocessMetadata}
            />
          ) : (
            <div className="placeholder">
              No image selected. Click "Select Image" to upload.
            </div>
          )}
        </div>
        
        <div className="detections-section">
          <h2>Detections ({detections.length})</h2>
          {detections.length > 0 ? (
            <DetectionList
              detections={detections}
              selectedIndex={selectedDetectionIndex}
              onDetectionClick={(index) => setSelectedDetectionIndex(index)}
            />
          ) : (
            <div className="placeholder">
              No detections yet. Upload an image and click "Detect".
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// YOLO output processing (copied from your working solution)
function processYOLOOutput(output, metadata, confThreshold = 0.45, nmsThreshold = 0.45) {
  const detections = [];
  const data = output.data;
  const dims = output.dims;
  
  const numFeatures = dims[1]; // 1427
  const numDetections = dims[2]; // 33600
  const numClasses = numFeatures - 4; // 1423
  
  for (let i = 0; i < numDetections; i++) {
    const cx = data[0 * numDetections + i];
    const cy = data[1 * numDetections + i];
    const width = data[2 * numDetections + i];
    const height = data[3 * numDetections + i];
    
    if (width <= 0 || height <= 0 || cx < 0 || cy < 0 || cx > metadata.modelSize || cy > metadata.modelSize) {
      continue;
    }
    
    let maxScore = -Infinity;
    let maxClass = 0;
    
    for (let c = 0; c < numClasses; c++) {
      const scoreIndex = (4 + c) * numDetections + i;
      const score = data[scoreIndex];
      if (score > maxScore) {
        maxScore = score;
        maxClass = c;
      }
    }
    
    if (maxScore > confThreshold) {
      // Keep in CENTER format (cx, cy, w, h) in model pixels (0-1280)
      detections.push({
        cx: cx,        // Center X in model pixels
        cy: cy,        // Center Y in model pixels
        width: width,  // Width in model pixels
        height: height, // Height in model pixels
        confidence: maxScore,
        class: maxClass,
        label: `Product #${maxClass}`,
      });
    }
  }
  
  // Sort by confidence
  detections.sort((a, b) => b.confidence - a.confidence);
  
  // Apply NMS
  return nms(detections, nmsThreshold);
}

function nms(detections, iouThreshold) {
  const keep = [];
  const indices = [...Array(detections.length).keys()];
  
  while (indices.length > 0) {
    const current = indices[0];
    keep.push(detections[current]);
    indices.shift();
    
    if (indices.length === 0) break;
    
    const newIndices = [];
    for (const idx of indices) {
      const iou = calculateIoU(detections[current], detections[idx]);
      if (iou <= iouThreshold) {
        newIndices.push(idx);
      }
    }
    indices.length = 0;
    indices.push(...newIndices);
  }
  
  return keep;
}

function calculateIoU(box1, box2) {
  // Convert center format to corners
  const x1_1 = box1.cx - box1.width / 2;
  const y1_1 = box1.cy - box1.height / 2;
  const x2_1 = box1.cx + box1.width / 2;
  const y2_1 = box1.cy + box1.height / 2;
  
  const x1_2 = box2.cx - box2.width / 2;
  const y1_2 = box2.cy - box2.height / 2;
  const x2_2 = box2.cx + box2.width / 2;
  const y2_2 = box2.cy + box2.height / 2;
  
  const x1 = Math.max(x1_1, x1_2);
  const y1 = Math.max(y1_1, y1_2);
  const x2 = Math.min(x2_1, x2_2);
  const y2 = Math.min(y2_1, y2_2);
  
  const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const area1 = box1.width * box1.height;
  const area2 = box2.width * box2.height;
  const union = area1 + area2 - intersection;
  
  return union > 0 ? intersection / union : 0;
}

export default TestModel;

